import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { LineChartPro } from "@mui/x-charts-pro/LineChartPro";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChartPoint } from "../models/chartDataset";
import { DiagnosticSummary } from "../models/summariseDiagnostics";
import { safeLocale } from "@/utilities/i18n/safeLocale";

/** Height of one chart when each measurement gets its own. */
const SEPARATE_CHART_HEIGHT = 210;

interface Props {
  points: ChartPoint[];
  /** The measurements being charted, in the order they should be drawn. */
  series: DiagnosticSummary[];
  /** Which of `series` are boolean/state readings, charted on their own axis instead of the numeric one. */
  booleanDiagnosticIds: Set<string>;
}

/**
 * The selected measurements over time.
 *
 * One chart by default; on request one per measurement, because two readings with different
 * units — a temperature and an engine speed — share an axis badly enough that the smaller one
 * becomes a flat line along the bottom.
 */
export function MotorDataChart({ points, series, booleanDiagnosticIds }: Props) {
  const { t, i18n } = useTranslation();
  const [separate, setSeparate] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!isFullscreen) {
      return;
    }

    // Escape, because in full screen the button that got you here is the only way out and it is
    // not where a reader looks first.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsFullscreen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isFullscreen]);

  const numericSeries = useMemo(
    () => series.filter(summary => !booleanDiagnosticIds.has(summary.diagnosticId)),
    [series, booleanDiagnosticIds],
  );
  const stateSeries = useMemo(
    () => series.filter(summary => booleanDiagnosticIds.has(summary.diagnosticId)),
    [series, booleanDiagnosticIds],
  );

  const xAxis = useMemo(
    () => [
      {
        dataKey: "timestamp",
        scaleType: "linear" as const,
        tickNumber: 6,
        valueFormatter: (value: unknown) =>
          new Date(Number(value)).toLocaleString(safeLocale(i18n.language), {
            hour: "2-digit",
            minute: "2-digit",
            day: "2-digit",
            month: "2-digit",
          }),
        zoom: { panning: true, minSpan: 2, step: 2 },
      },
    ],
    [i18n.language],
  );

  const yAxis = useMemo(
    () =>
      stateSeries.length > 0
        ? [
            { id: "value" },
            {
              id: "state",
              min: 0,
              max: 1,
              tickNumber: 2,
              valueFormatter: (value: unknown) =>
                Number(value) ? t("motorData.stateOn") : t("motorData.stateOff"),
            },
          ]
        : undefined,
    [stateSeries.length, t],
  );

  const label = (summary: DiagnosticSummary) =>
    summary.unit ? `${summary.name} (${summary.unit})` : summary.name;

  const combinedSeries = useMemo(
    () => [
      ...numericSeries.map(summary => ({
        dataKey: summary.diagnosticId,
        label: label(summary),
        showMark: false,
        // Rows carry only the diagnostics that reported at that instant; join across the gaps.
        connectNulls: true,
        curve: "linear" as const,
        ...(stateSeries.length > 0 ? { yAxisId: "value" } : {}),
      })),
      ...stateSeries.map(summary => ({
        dataKey: summary.diagnosticId,
        label: label(summary),
        showMark: false,
        // Rows carry only the diagnostics that reported at that instant; join across the gaps.
        connectNulls: true,
        curve: "stepAfter" as const,
        yAxisId: "state",
      })),
    ],
    [numericSeries, stateSeries],
  );

  return (
    <Box
      sx={
        isFullscreen
          ? {
              position: "fixed",
              inset: 0,
              zIndex: theme => theme.zIndex.modal,
              bgcolor: "background.default",
              p: 2,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }
          : {
              mt: 2,
              p: 1,
              height: 380,
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              border: theme => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
            }
      }
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1 }}>
        <Typography variant="subtitle2">{t("motorData.chartTitle")}</Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          {series.length > 1 && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={separate}
                  onChange={(_event, checked) => setSeparate(checked)}
                />
              }
              label={t("motorData.separateCharts")}
              sx={{ m: 0 }}
            />
          )}
          <Tooltip
            title={
              isFullscreen
                ? t("motorData.exitFullscreen")
                : t("motorData.enterFullscreen")
            }
          >
            <IconButton size="small" onClick={() => setIsFullscreen(current => !current)}>
              {isFullscreen ? (
                <FullscreenExitIcon fontSize="small" />
              ) : (
                <FullscreenIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        {points.length === 0 ? (
          <Box sx={{ display: "grid", placeItems: "center", height: "100%" }}>
            <Typography color="text.secondary">
              {t("motorData.noChartData")}
            </Typography>
          </Box>
        ) : separate && series.length > 1 ? (
          <Box sx={{ height: "100%", overflowY: "auto", pr: 1 }}>
            {series.map(summary => (
              <Box
                key={summary.diagnosticId}
                sx={{
                  height: SEPARATE_CHART_HEIGHT,
                  mb: 1,
                  p: 0.5,
                  border: theme => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  bgcolor: "background.paper",
                }}
              >
                <Typography variant="caption" sx={{ px: 1, display: "block" }}>
                  {label(summary)}
                </Typography>
                <LineChartPro
                  dataset={points}
                  series={[
                    {
                      dataKey: summary.diagnosticId,
                      label: label(summary),
                      showMark: false,
                      // Rows carry only the diagnostics that reported at that instant; join across the gaps.
                      connectNulls: true,
                      curve: booleanDiagnosticIds.has(summary.diagnosticId)
                        ? "stepAfter"
                        : "linear",
                    },
                  ]}
                  xAxis={xAxis}
                  height={SEPARATE_CHART_HEIGHT - 38}
                  margin={{ left: 70, right: 16, top: 8, bottom: 34 }}
                  grid={{ vertical: false, horizontal: true }}
                />
              </Box>
            ))}
          </Box>
        ) : (
          <LineChartPro
            dataset={points}
            series={combinedSeries}
            xAxis={xAxis}
            yAxis={yAxis}
            rightAxis={stateSeries.length > 0 ? "state" : undefined}
            margin={{ left: 70, right: 24, top: 16, bottom: 42 }}
            grid={{ vertical: false, horizontal: true }}
          />
        )}
      </Box>
    </Box>
  );
}
